export interface ServiceLocation {
  id: string;
  name: string;
  slug: string;
  latitude: number;
  longitude: number;
  meta_description: string;
  is_primary: boolean;
  display_order: number;
  is_active: boolean;
  county: string | null;
}

// Calculate distance between two coordinates (Haversine formula)
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}

// Find nearest location from user's coordinates
export function findNearestLocation(
  userLat: number,
  userLon: number,
  locations: ServiceLocation[]
): ServiceLocation | null {
  if (locations.length === 0) return null;

  let nearest = locations[0];
  let minDistance = calculateDistance(
    userLat,
    userLon,
    nearest.latitude,
    nearest.longitude
  );

  for (const location of locations) {
    const distance = calculateDistance(
      userLat,
      userLon,
      location.latitude,
      location.longitude
    );
    if (distance < minDistance) {
      minDistance = distance;
      nearest = location;
    }
  }

  // Only return if within 80 miles
  return minDistance <= 80 ? nearest : null;
}

// Generate location-specific text
export function getLocationText(
  baseText: string,
  locationName: string | null
): string {
  if (!locationName) return baseText;
  return baseText.replace('{location}', locationName);
}

// Generate location-specific meta title
export function getLocationMetaTitle(
  serviceTitle: string,
  locationName: string | null
): string {
  if (!locationName) {
    return `${serviceTitle} | EvoHome Improvements`;
  }
  return `${serviceTitle} in ${locationName} | EvoHome Improvements`;
}

// Generate location-specific H1
export function getLocationH1(
  serviceTitle: string,
  locationName: string | null
): string {
  if (!locationName) return serviceTitle;
  return `${serviceTitle} in ${locationName}`;
}

// Generate location-specific description
export function getLocationDescription(
  baseDescription: string,
  locationName: string | null,
  locationMeta?: string
): string {
  if (!locationName) return baseDescription;
  if (locationMeta) return locationMeta;
  return `${baseDescription} Serving ${locationName} and surrounding areas.`;
}
